import React, { useState, useEffect } from 'react';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import { assetsApi, taxConfigApi } from '../../../services/financeApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';

const PurchaseOrderForm = ({ open, onClose, onSubmit, initialData = null, isEditing = false, presetPurchaseRequestId = '' }) => {
  const [formData, setFormData] = useState({
    supplier: '',
    warehouse: '',
    procurementType: 'INVENTORY',
    items: [],
    deliveryAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    requiredBy: '',
    priority: 'normal',
    approvalRequired: true,
    terms: {
      paymentTerms: '',
      deliveryTerms: '',
      taxRate: 0,
      shippingCost: 0,
      otherCharges: 0
    },
    notes: '',
    costCenter: '',
    costCenterLabel: ''
  });

  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = useState('');
  const [loading, setLoading] = useState(false);
  const [prLoading, setPrLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);
  // When approval levels are enforced the server ignores approvalRequired, so
  // the checkbox is shown locked rather than pretending it still has an effect.
  const [approvalLevelsEnabled, setApprovalLevelsEnabled] = useState(false);

  const [ssclEnabled, setSsclEnabled] = useState(false);
  const [ssclRate, setSsclRate] = useState('2.5');
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState('');
  const [ssclAmount, setSsclAmount] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [taxDefaults, setTaxDefaults] = useState({ ssclRate: '2.5', vatRate: '' });

  const unitOptions = [
    { value: 'PCS', label: 'Pieces (PCS)' },
    { value: 'KG', label: 'Kilograms (KG)' },
    { value: 'G', label: 'Grams (G)' },
    { value: 'L', label: 'Liters (L)' },
    { value: 'ML', label: 'Milliliters (ML)' },
    { value: 'M', label: 'Meters (M)' },
    { value: 'CM', label: 'Centimeters (CM)' },
    { value: 'SET', label: 'Set (SET)' },
    { value: 'BOX', label: 'Box (BOX)' },
    { value: 'PACK', label: 'Pack (PACK)' },
    { value: 'DOZEN', label: 'Dozen (DOZ)' },
    { value: 'PAIR', label: 'Pair (PAIR)' }
  ];

  // Are amount-based approval levels being enforced?
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    inventoryApi.getPOApprovalSettings()
      .then(res => { if (!cancelled) setApprovalLevelsEnabled(res?.data?.enabled === true); })
      .catch(() => { if (!cancelled) setApprovalLevelsEnabled(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Fetch SSCL and VAT rates from tax configurations on mount
  useEffect(() => {
    taxConfigApi.getTaxConfigs({ isActive: true, limit: 100 })
      .then(res => {
        const configs = res.data?.data?.taxConfigs || [];
        const ssclConfig = configs.find(c =>
          c.code === 'SSCL' || c.name?.toUpperCase().includes('SSCL')
        );
        const vatConfig = configs.find(c =>
          c.code === 'VAT' || c.name?.toUpperCase().includes('VAT')
        );
        const defaults = {
          ssclRate: ssclConfig ? String(ssclConfig.percentage) : '2.5',
          vatRate: vatConfig ? String(vatConfig.percentage) : '',
        };
        setTaxDefaults(defaults);
      })
      .catch(err => {
        console.warn('[PurchaseOrderForm] could not fetch tax configs:', err.message);
      });
  }, []);

  const resetForm = () => {
    setFormData({
      supplier: '',
      warehouse: '',
      procurementType: 'INVENTORY',
      items: [],
      deliveryAddress: { street: '', city: '', state: '', postalCode: '', country: '' },
      requiredBy: '',
      priority: 'normal',
      approvalRequired: true,
      terms: { paymentTerms: '', deliveryTerms: '', taxRate: 0, shippingCost: 0, otherCharges: 0 },
      notes: '',
      costCenter: '',
      costCenterLabel: ''
    });
    setSelectedPurchaseRequest('');
    setErrors({});
    setShowDeliveryAddress(false);
    // dataLoading is cleared by the loader effect once every fetch settles
  };

  useEffect(() => {
    if (open) {
      setDataLoading(true);

      const prId = initialData
        ? (typeof (initialData.purchaseRequest?._id || initialData.purchaseRequest) === 'object'
            ? (initialData.purchaseRequest?._id || initialData.purchaseRequest)?._id
            : (initialData.purchaseRequest?._id || initialData.purchaseRequest || initialData.metadata?.basedOnPurchaseRequest || ''))
        : '';

      // Load base data + inject the currently-linked PR (may be converted_to_po) so it shows in dropdown
      const prListPromise = loadPurchaseRequests().then(approvedList => {
        if (!prId) return;
        const alreadyIn = (approvedList || []).some(pr => String(pr._id) === String(prId));
        if (alreadyIn) return;
        inventoryApi.getPurchaseRequestById(prId)
          .then(res => {
            const pr = res.data;
            if (pr) setPurchaseRequests(prev => [pr, ...prev]);
          })
          .catch(() => {});
      });

      const baseDataPromise = Promise.all([
        loadWarehouses(), loadItems(), loadAssets(), prListPromise
      ]);

      if (initialData) {
        const mappedItems = (initialData.lineItems || initialData.items || []).map(lineItem => ({
          item: lineItem.item?._id || lineItem.item || '',
          assetId: lineItem.assetId?._id || lineItem.assetId || '',
          assetCode: lineItem.assetCode || '',
          assetName: lineItem.assetName || '',
          quantity: lineItem.quantity || 0,
          unit: lineItem.unit || 'PCS',
          unitPrice: lineItem.unitPrice || 0,
          notes: lineItem.notes || '',
          specifications: lineItem.specifications || lineItem.description || ''
        }));

        setFormData({
          supplier: initialData.supplier?._id || initialData.supplier || '',
          warehouse: initialData.warehouse?._id || initialData.warehouse || '',
          procurementType: initialData.procurementType || 'INVENTORY',
          items: mappedItems,
          deliveryAddress: initialData.deliveryAddress || { street: '', city: '', state: '', postalCode: '', country: '' },
          requiredBy: initialData.expectedDeliveryDate ? new Date(initialData.expectedDeliveryDate).toISOString().split('T')[0] :
            (initialData.requiredBy ? new Date(initialData.requiredBy).toISOString().split('T')[0] : ''),
          priority: initialData.priority || 'normal',
          approvalRequired: initialData.approvalRequired !== false,
          terms: {
            paymentTerms: initialData.paymentTerms || initialData.customPaymentTerms || initialData.terms?.paymentTerms || '',
            deliveryTerms: initialData.deliveryTerms || initialData.customDeliveryTerms || initialData.terms?.deliveryTerms || '',
            taxRate: initialData.terms?.taxRate || 0,
            shippingCost: initialData.totals?.shippingCost || initialData.terms?.shippingCost || 0,
            otherCharges: initialData.totals?.otherCharges || initialData.terms?.otherCharges || 0
          },
          notes: initialData.notes || initialData.metadata?.notes || '',
          costCenter: initialData.costCenter || '',
          costCenterLabel: initialData.costCenterLabel || ''
        });

        setSelectedPurchaseRequest(prId);

        // Restore enabled state from saved totals; rates are synced from tax config below
        const savedSsclAmt = parseFloat(initialData.totals?.ssclAmount) || 0;
        const savedVatAmt  = parseFloat(initialData.totals?.vatAmount)  || 0;
        setSsclEnabled(savedSsclAmt > 0);
        setVatEnabled(savedVatAmt > 0);

      } else {
        resetForm();
        setSsclEnabled(false);
        setVatEnabled(false);
      }

      // Opened from a purchase request (Convert to PO) — preload it through the same path
      // the dropdown uses so the mapping stays in one place. Started after resetForm() so
      // the reset cannot clear the link, but in parallel with the lists above: it only
      // needs the request itself, not the item/warehouse catalogues.
      const presetPromise = (presetPurchaseRequestId && !initialData)
        ? handlePurchaseRequestSelection(presetPurchaseRequestId)
        : Promise.resolve();

      Promise.all([baseDataPromise, presetPromise])
        .finally(() => setDataLoading(false));
    }
    // taxDefaults is deliberately excluded: it arrives from an async fetch and would
    // otherwise re-trigger every loader (and the linked-PR preload) a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData, presetPurchaseRequestId]);

  // Rates always come from tax config, so keep them in sync independently of the loaders
  useEffect(() => {
    setSsclRate(taxDefaults.ssclRate);
    setVatRate(taxDefaults.vatRate);
  }, [taxDefaults]);


  const loadWarehouses = async () => {
    try {
      const response = await adminApi.getLocations({ type: 'warehouse', limit: 1000 });
      const warehousesData = response.data?.data || response.data || [];
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      try {
        const fallbackResponse = await adminApi.getLocations({ limit: 1000 });
        const allLocations = fallbackResponse.data?.data || fallbackResponse.data || [];
        setWarehouses(allLocations.filter(location => location.type === 'warehouse'));
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        setWarehouses([]);
      }
    }
  };

  const loadItems = async () => {
    try {
      const response = await inventoryApi.getItems({ limit: 1000 });
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await assetsApi.getAll({ status: 'ACTIVE', limit: 200 });
      setAssets(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error loading fixed assets:', error);
      setAssets([]);
    }
  };

  const loadPurchaseRequests = async () => {
    try {
      const response = await inventoryApi.getPurchaseRequests({ status: 'approved', limit: 1000 });
      const list = response.data || [];
      setPurchaseRequests(list);
      return list;
    } catch (error) {
      console.error('Error loading purchase requests:', error);
      setPurchaseRequests([]);
      return [];
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier) newErrors.supplier = 'Supplier is required';
    if (!formData.warehouse) newErrors.warehouse = 'Warehouse is required';
    if (!formData.costCenter) newErrors.costCenter = 'Cost center is required';

    if (!formData.requiredBy) {
      newErrors.requiredBy = 'Expected delivery date is required';
    } else {
      const today = new Date().toISOString().split('T')[0];
      if (formData.requiredBy < today) {
        newErrors.requiredBy = 'Expected delivery date cannot be in the past';
      }
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
      formData.items.forEach((item, index) => {
        if (isFixedAsset) {
          if (!item.assetId) newErrors[`item_${index}`] = 'Asset is required';
        } else if (!item.item) {
          newErrors[`item_${index}`] = 'Item is required';
        }
        if (!item.quantity || item.quantity <= 0) newErrors[`quantity_${index}`] = 'Quantity must be greater than 0';
        if (item.unitPrice === undefined || item.unitPrice < 0) newErrors[`unitPrice_${index}`] = 'Unit price must be 0 or greater';
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePurchaseRequestSelection = async (prId) => {
    setSelectedPurchaseRequest(prId);

    if (!prId) {
      setFormData(prev => ({ ...prev, items: [] }));
      return;
    }

    try {
      setPrLoading(true);
      const response = await inventoryApi.getPurchaseRequestById(prId);
      const purchaseRequest = response.data;

      const mappedItems = (purchaseRequest.lineItems || []).map(lineItem => {
        const itemData = lineItem.item;
        const itemId = typeof itemData === 'object' ? itemData._id : itemData;
        const unitOfMeasure = typeof itemData === 'object' ? itemData.unitOfMeasure : 'PCS';
        const unitCost = typeof itemData === 'object' ? (itemData.pricing?.sellingPrice || itemData.pricing?.unitCost || 0) : 0;

        return {
          item: itemId || '',
          assetId: lineItem.assetId?._id || lineItem.assetId || '',
          assetCode: lineItem.assetCode || '',
          assetName: lineItem.assetName || '',
          quantity: lineItem.quantity || lineItem.quantityRequested || 1,
          unit: lineItem.unit || unitOfMeasure || 'PCS',
          unitPrice: lineItem.estimatedUnitPrice || unitCost || 0,
          notes: lineItem.notes || '',
          specifications: lineItem.specifications || lineItem.itemDescription || lineItem.description || ''
        };
      });

      setFormData(prev => ({
        ...prev,
        supplier: purchaseRequest.supplier?._id || purchaseRequest.supplier || prev.supplier,
        procurementType: purchaseRequest.procurementType || prev.procurementType,
        costCenter: purchaseRequest.costCenter || prev.costCenter,
        costCenterLabel: purchaseRequest.costCenterLabel || prev.costCenterLabel,
        items: mappedItems,
        notes: purchaseRequest.notes ?
          `Based on Purchase Request: ${purchaseRequest.prNumber}\n\nPR Notes: ${purchaseRequest.notes}${prev.notes ? '\n\nAdditional Notes: ' + prev.notes : ''}` :
          `Based on Purchase Request: ${purchaseRequest.prNumber}${prev.notes ? '\n\n' + prev.notes : ''}`,
        priority: purchaseRequest.priority || prev.priority,
        requiredBy: purchaseRequest.requiredByDate ?
          new Date(purchaseRequest.requiredByDate).toISOString().split('T')[0] :
          (purchaseRequest.requestDate ?
            new Date(new Date(purchaseRequest.requestDate).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
            prev.requiredBy)
      }));
    } catch (error) {
      console.error('Error loading purchase request details:', error);
      setErrors({ submit: 'Failed to load purchase request details. Please try again.' });
      setSelectedPurchaseRequest('');
    } finally {
      setPrLoading(false);
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item: '', assetId: '', assetCode: '', assetName: '', quantity: 1, unitPrice: 0, unit: 'PCS', notes: '' }]
    }));
  };

  const handleProcurementTypeChange = (value) => {
    // Switching type invalidates existing line refs — reset the items list
    setFormData(prev => ({ ...prev, procurementType: value, items: [] }));
    setErrors(prev => ({ ...prev, items: '' }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const updatedItem = { ...item, [field]: value };
        if (field === 'item') {
          const selectedItem = items.find(it => it._id === value);
          if (selectedItem) {
            updatedItem.unitPrice = selectedItem.pricing?.sellingPrice || selectedItem.pricing?.unitCost || 0;
            updatedItem.unit = selectedItem.inventory?.unit || selectedItem.unitOfMeasure || 'PCS';
          }
        }
        if (field === 'assetId') {
          const selectedAsset = assets.find(a => a._id === value);
          if (selectedAsset) {
            updatedItem.assetCode = selectedAsset.code || '';
            updatedItem.assetName = selectedAsset.name || '';
            updatedItem.unitPrice = selectedAsset.purchasePrice || selectedAsset.marketPrice || 0;
          }
        }
        return updatedItem;
      })
    }));
    if (errors[`${field}_${index}`]) setErrors(prev => ({ ...prev, [`${field}_${index}`]: '' }));
  };

  const calculateSubtotal = () => formData.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const calculateTotal = () => calculateSubtotal() + ssclAmount + vatAmount + (formData.terms.shippingCost || 0) + (formData.terms.otherCharges || 0);

  useEffect(() => {
    const subtotal = calculateSubtotal();
    const newSscl = ssclEnabled ? (subtotal * (parseFloat(ssclRate) || 0) / 100) : 0;
    const vatBase = subtotal + newSscl;
    const newVat = vatEnabled ? (vatBase * (parseFloat(vatRate) || 0) / 100) : 0;
    setSsclAmount(newSscl);
    setVatAmount(newVat);
  }, [formData.items, ssclEnabled, ssclRate, vatEnabled, vatRate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const subtotal = calculateSubtotal();
      const shippingCost = formData.terms.shippingCost || 0;
      const otherCharges = formData.terms.otherCharges || 0;
      const grandTotal = subtotal + ssclAmount + vatAmount + shippingCost + otherCharges;

      const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
      const submitData = {
        supplier: formData.supplier,
        warehouse: formData.warehouse,
        procurementType: formData.procurementType,
        items: formData.items.map(item => (
          isFixedAsset
            ? {
                assetId: item.assetId,
                assetCode: item.assetCode || '',
                assetName: item.assetName || '',
                quantity: item.quantity,
                unit: item.unit,
                unitPrice: item.unitPrice,
                specifications: item.specifications || '',
                notes: item.notes || ''
              }
            : {
                item: item.item,
                quantity: item.quantity,
                unit: item.unit,
                unitPrice: item.unitPrice,
                specifications: item.specifications || '',
                notes: item.notes || ''
              }
        )),
        deliveryAddress: formData.deliveryAddress,
        requiredBy: formData.requiredBy ? new Date(formData.requiredBy).toISOString() : null,
        priority: formData.priority,
        approvalRequired: formData.approvalRequired,
        terms: {
          paymentTerms: formData.terms.paymentTerms || '',
          deliveryTerms: formData.terms.deliveryTerms || ''
        },
        totals: {
          subtotal,
          totalDiscount: 0,
          ssclAmount: parseFloat(ssclAmount.toFixed(2)),
          ssclRate: ssclEnabled ? parseFloat(ssclRate) || 0 : 0,
          vatAmount: parseFloat(vatAmount.toFixed(2)),
          vatRate: vatEnabled ? parseFloat(vatRate) || 0 : 0,
          shippingCost,
          otherCharges,
          grandTotal: parseFloat(grandTotal.toFixed(2))
        },
        notes: formData.notes || '',
        costCenter: formData.costCenter,
        costCenterLabel: formData.costCenterLabel || '',
        ...(selectedPurchaseRequest && {
          purchaseRequest: selectedPurchaseRequest,
          metadata: { basedOnPurchaseRequest: selectedPurchaseRequest }
        })
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: error.response?.data?.message || error.message || 'Failed to save purchase order' });
    } finally {
      setLoading(false);
    }
  };

  // A purchase request may carry no supplier — in that case the supplier is settled here,
  // at purchase order level, so such requests stay selectable whatever the supplier is.
  const selectedPRObject = selectedPurchaseRequest
    ? purchaseRequests.find(pr => String(pr._id) === String(selectedPurchaseRequest)) || null
    : null;
  const selectedPRSupplierId = selectedPRObject
    ? String(selectedPRObject.supplier?._id || selectedPRObject.supplier || '')
    : '';
  const supplierLockedByPR = Boolean(selectedPurchaseRequest && selectedPRSupplierId);
  const supplierNeededForPR = Boolean(selectedPurchaseRequest && !selectedPRSupplierId && !formData.supplier);

  const filteredPurchaseRequests = purchaseRequests.filter(pr => {
    // Always include the currently-selected PR so it shows on edit screen
    if (selectedPurchaseRequest && String(pr._id) === String(selectedPurchaseRequest)) return true;
    const prSupplierId = pr.supplier?._id || pr.supplier;
    // Requests without a supplier can be ordered from anyone
    if (!prSupplierId) return true;
    if (!formData.supplier) return true;
    return String(prSupplierId) === String(formData.supplier);
  });

  if (!open) return null;

  const inputCls = (hasError) =>
    `w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">

          {/* Data loading overlay (edit mode) */}
          {dataLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center gap-3">
              <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium text-indigo-600">Loading order details…</span>
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white">
            <form onSubmit={handleSubmit} className="px-4 pt-3 pb-2">

              {/* Error Summary */}
              {errors.submit && (
                <div className="mb-2 bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-700">{errors.submit}</p>
                </div>
              )}

              {/* Header Fields — compact 5-col row */}
              <div className="grid grid-cols-5 gap-2 mb-2">
                {/* Supplier */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Supplier <span className="text-red-500">*</span></label>
                  <SearchableInventorySupplierDropdown
                    value={formData.supplier}
                    onChange={(id) => {
                      // Keep the linked request when it has no supplier of its own, or when it
                      // still matches; otherwise the link no longer holds and is cleared.
                      const keepPR = Boolean(selectedPurchaseRequest) &&
                        (!selectedPRSupplierId || selectedPRSupplierId === String(id));
                      setFormData(prev => ({ ...prev, supplier: id, items: keepPR ? prev.items : [] }));
                      if (!keepPR) setSelectedPurchaseRequest('');
                      if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
                    }}
                    placeholder="Select supplier"
                    error={!!errors.supplier}
                    disabled={supplierLockedByPR}
                    menuPlacement="down"
                  />
                  {errors.supplier && <p className="text-xs text-red-500 mt-0.5">{errors.supplier}</p>}
                  {supplierLockedByPR && (
                    <p className="text-[10px] text-gray-400 mt-0.5">Inherited from the linked purchase request</p>
                  )}
                  {supplierNeededForPR && (
                    <p className="text-[10px] text-amber-600 mt-0.5">This request has no supplier — choose one for this order</p>
                  )}
                </div>

                {/* Warehouse */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Warehouse <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.warehouse}
                    onChange={(e) => handleInputChange('warehouse', e.target.value)}
                    options={warehouses.map(w => ({ value: w._id, label: `${w.name} (${w.code})` }))}
                    placeholder="Select warehouse"
                    error={errors.warehouse || ''}
                    size="sm"
                  />
                  {errors.warehouse && <p className="text-xs text-red-500 mt-0.5">{errors.warehouse}</p>}
                </div>

                {/* Required By */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Required By <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.requiredBy}
                    onChange={(e) => handleInputChange('requiredBy', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={inputCls(!!errors.requiredBy)}
                  />
                  {errors.requiredBy && <p className="text-xs text-red-500 mt-0.5">{errors.requiredBy}</p>}
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className={inputCls(false)}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Cost Center row */}
              <div className="grid grid-cols-5 gap-2 mb-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Cost Center <span className="text-red-500">*</span></label>
                  <SearchableCostCenterDropdown
                    value={formData.costCenter}
                    displayLabel={formData.costCenterLabel}
                    onChange={(id, label) => {
                      handleInputChange('costCenter', id);
                      handleInputChange('costCenterLabel', label || '');
                    }}
                    placeholder="Select cost center"
                    error={!!errors.costCenter}
                    limit={50}
                    menuPlacement="down"
                  />
                  {errors.costCenter && <p className="text-xs text-red-500 mt-0.5">{errors.costCenter}</p>}
                </div>

                {/* Procurement Type */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Procurement Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.procurementType}
                    onChange={(e) => handleProcurementTypeChange(e.target.value)}
                    disabled={!!selectedPurchaseRequest}
                    className={`${inputCls(false)} ${selectedPurchaseRequest ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                    title={selectedPurchaseRequest ? 'Inherited from the linked purchase request' : ''}
                  >
                    <option value="INVENTORY">Inventory</option>
                    <option value="FIXED_ASSET">Fixed Asset</option>
                  </select>
                </div>
              </div>

              {/* Secondary row: Purchase Request + Approval */}
              <div className="grid grid-cols-5 gap-2 mb-3">
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-600">
                      Purchase Request <span className="text-[10px] text-gray-400 font-normal">(Optional)</span>
                    </label>
                    {selectedPurchaseRequest && (
                      <button
                        type="button"
                        onClick={() => handlePurchaseRequestSelection('')}
                        className="text-[10px] text-red-500 hover:text-red-700 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <SearchableSelect
                    value={selectedPurchaseRequest}
                    onChange={(e) => handlePurchaseRequestSelection(e.target.value)}
                    options={filteredPurchaseRequests.map(pr => ({
                      value: pr._id,
                      label: `${pr.prNumber} — ${pr.lineItems?.length || 0} items`
                    }))}
                    placeholder={
                      filteredPurchaseRequests.length === 0
                        ? (formData.supplier ? 'No approved PRs for this supplier' : 'No approved PRs available')
                        : 'Select a purchase request (optional)'
                    }
                    disabled={prLoading}
                    size="sm"
                  />
                </div>

                {/* Approval Required — forced on while approval levels are enforced */}
                <div className="col-span-2 flex items-end pb-2">
                  <label className={`flex items-center gap-2 ${approvalLevelsEnabled ? '' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={approvalLevelsEnabled ? true : formData.approvalRequired}
                      disabled={approvalLevelsEnabled}
                      onChange={(e) => handleInputChange('approvalRequired', e.target.checked)}
                      className="h-3 w-3 text-indigo-600 border-gray-300 rounded disabled:opacity-60"
                    />
                    <span className="text-xs font-medium text-gray-600">Approval Required</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${(approvalLevelsEnabled || formData.approvalRequired) ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                      {(approvalLevelsEnabled || formData.approvalRequired) ? 'Yes' : 'No'}
                    </span>
                    {approvalLevelsEnabled && (
                      <span className="text-[10px] text-gray-500">
                        Required — approval levels are configured for purchase orders
                      </span>
                    )}
                  </label>
                </div>
              </div>

              {/* Delivery Address — collapsible */}
              <div className="mb-3">
                <button
                  type="button"
                  onClick={() => setShowDeliveryAddress(v => !v)}
                  className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors mb-1"
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${showDeliveryAddress ? 'rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Delivery Address <span className="text-[10px] text-gray-400 font-normal">(Optional)</span>
                </button>
                {showDeliveryAddress && (
                  <div className="grid grid-cols-5 gap-2">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Street</label>
                      <input
                        type="text"
                        value={formData.deliveryAddress.street}
                        onChange={(e) => handleInputChange('deliveryAddress.street', e.target.value)}
                        placeholder="Street address"
                        className={inputCls(false)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                      <input type="text" value={formData.deliveryAddress.city}
                        onChange={(e) => handleInputChange('deliveryAddress.city', e.target.value)}
                        placeholder="City" className={inputCls(false)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                      <input type="text" value={formData.deliveryAddress.state}
                        onChange={(e) => handleInputChange('deliveryAddress.state', e.target.value)}
                        placeholder="State" className={inputCls(false)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Postal / Country</label>
                      <div className="flex gap-1">
                        <input type="text" value={formData.deliveryAddress.postalCode}
                          onChange={(e) => handleInputChange('deliveryAddress.postalCode', e.target.value)}
                          placeholder="Postal" className={`${inputCls(false)} w-1/2`} />
                        <input type="text" value={formData.deliveryAddress.country}
                          onChange={(e) => handleInputChange('deliveryAddress.country', e.target.value)}
                          placeholder="Country" className={`${inputCls(false)} w-1/2`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Items Section — styled like BillEntry line items */}
              <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
                {/* Gradient header */}
                <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
                  <span className="text-xs font-semibold text-white">
                    Order Items ({formData.items.length})
                    {selectedPurchaseRequest && (
                      <span className="ml-2 text-[10px] text-indigo-200 font-normal">— loaded from PR</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={prLoading}
                    className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                </div>

                {/* Scrollable rows */}
                <div className="max-h-[36vh] overflow-y-auto divide-y divide-gray-100">
                  {prLoading ? (
                    <div className="px-3 py-8 flex flex-col items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-xs text-indigo-500 animate-pulse">Loading items from purchase request…</span>
                    </div>
                  ) : formData.items.length === 0 ? (
                    <div className="px-3 py-8 text-center text-xs text-gray-400">
                      No items added yet. Click "Add" to add order items.
                    </div>
                  ) : (
                    formData.items.map((item, index) => (
                      <div key={index} className="px-3 py-2 hover:bg-gray-50/60 transition-colors relative">
                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="absolute right-1 top-3 w-4 h-4 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
                          title="Remove"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>

                        {/* Item row grid */}
                        <div className="grid grid-cols-[2fr_0.45fr_0.7fr_0.7fr_1fr] gap-2 pr-5">
                          {/* Item / Asset */}
                          {formData.procurementType === 'FIXED_ASSET' ? (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Fixed Asset <span className="text-red-500">*</span></label>
                              <SearchableSelect
                                value={item.assetId}
                                onChange={(e) => handleItemChange(index, 'assetId', e.target.value)}
                                options={assets.map(a => ({ value: a._id, label: `${a.name}${a.code ? ` (${a.code})` : ''}${a.serialNumber ? ` — ${a.serialNumber}` : ''}` }))}
                                placeholder="Select asset…"
                                error={errors[`item_${index}`] || ''}
                                size="sm"
                              />
                              {errors[`item_${index}`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`item_${index}`]}</p>}
                            </div>
                          ) : (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Item <span className="text-red-500">*</span></label>
                              <SearchableSelect
                                value={item.item}
                                onChange={(e) => handleItemChange(index, 'item', e.target.value)}
                                options={items.map(i => ({ value: i._id, label: `${i.name} (${i.sku})` }))}
                                placeholder="Select item…"
                                error={errors[`item_${index}`] || ''}
                                size="sm"
                              />
                              {errors[`item_${index}`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`item_${index}`]}</p>}
                            </div>
                          )}

                          {/* Quantity */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Qty <span className="text-red-500">*</span></label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0" step="0.01"
                              className={`${inputCls(!!errors[`quantity_${index}`])} text-right`}
                            />
                            {errors[`quantity_${index}`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`quantity_${index}`]}</p>}
                          </div>

                          {/* Unit */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                            <select
                              value={item.unit}
                              disabled
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-50 text-gray-500 cursor-not-allowed"
                            >
                              {unitOptions.map(u => (
                                <option key={u.value} value={u.value}>{u.value}</option>
                              ))}
                            </select>
                          </div>

                          {/* Unit Price */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price</label>
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              min="0" step="0.01"
                              className={`${inputCls(!!errors[`unitPrice_${index}`])} text-right`}
                            />
                            {errors[`unitPrice_${index}`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`unitPrice_${index}`]}</p>}
                          </div>

                          {/* Notes */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                            <input
                              type="text"
                              value={item.notes}
                              onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                              placeholder="Notes…"
                              className={inputCls(false)}
                            />
                          </div>
                        </div>

                        {/* Line total strip */}
                        <div className="h-4 flex items-center justify-end mt-0.5">
                          <span className="text-[10px] text-gray-400">
                            Line Total: Rs {(item.quantity * item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {errors.items && (
                  <p className="text-xs text-red-500 px-3 py-1.5 border-t border-gray-100">{errors.items}</p>
                )}
              </div>

              {/* Footer: Totals | Notes + Terms | Actions */}
              <div className="flex items-end justify-between gap-4 pt-2 border-t border-gray-100">

                {/* Totals Panel */}
                <div className="w-64 border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                    <span className="text-xs text-gray-500">Sub Total</span>
                    <span className="text-xs font-semibold text-gray-700">
                      Rs {calculateSubtotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* SSCL */}
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id="po-ssclEnabled"
                        checked={ssclEnabled}
                        onChange={(e) => setSsclEnabled(e.target.checked)}
                        className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                      />
                      <label htmlFor="po-ssclEnabled" className="text-xs text-gray-600 cursor-pointer select-none">SSCL</label>
                      {ssclEnabled && parseFloat(ssclRate) > 0 && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{ssclRate}%</span>
                      )}
                    </div>
                    <span className={`text-xs font-semibold ${ssclEnabled ? 'text-gray-700' : 'text-gray-300'}`}>
                      Rs {ssclAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* VAT */}
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id="po-vatEnabled"
                        checked={vatEnabled}
                        onChange={(e) => setVatEnabled(e.target.checked)}
                        className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                      />
                      <label htmlFor="po-vatEnabled" className="text-xs text-gray-600 cursor-pointer select-none">VAT</label>
                      {vatEnabled && parseFloat(vatRate) > 0 && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{vatRate}%</span>
                      )}
                    </div>
                    <span className={`text-xs font-semibold ${vatEnabled ? 'text-gray-700' : 'text-gray-300'}`}>
                      Rs {vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Shipping */}
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                    <span className="text-xs text-gray-600">Shipping</span>
                    <input
                      type="number"
                      value={formData.terms.shippingCost || 0}
                      onChange={(e) => handleInputChange('terms.shippingCost', parseFloat(e.target.value) || 0)}
                      min="0" step="0.01" placeholder="0.00"
                      className="w-24 px-1 py-0.5 text-xs border border-gray-300 rounded text-right"
                    />
                  </div>

                  {/* Other Charges */}
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                    <span className="text-xs text-gray-600">Other</span>
                    <input
                      type="number"
                      value={formData.terms.otherCharges || 0}
                      onChange={(e) => handleInputChange('terms.otherCharges', parseFloat(e.target.value) || 0)}
                      min="0" step="0.01" placeholder="0.00"
                      className="w-24 px-1 py-0.5 text-xs border border-gray-300 rounded text-right"
                    />
                  </div>

                  {/* Grand Total */}
                  <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600">
                    <span className="text-[10px] font-bold text-indigo-100 uppercase tracking-wider">Grand Total</span>
                    <span className="text-sm font-black text-white">
                      Rs {calculateTotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Middle: Terms + Notes */}
                <div className="flex-1 grid grid-cols-2 gap-2 self-stretch">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Payment Terms</label>
                    <input
                      type="text"
                      value={formData.terms.paymentTerms}
                      onChange={(e) => handleInputChange('terms.paymentTerms', e.target.value)}
                      placeholder="e.g., Net 30"
                      className={inputCls(false)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Terms</label>
                    <input
                      type="text"
                      value={formData.terms.deliveryTerms}
                      onChange={(e) => handleInputChange('terms.deliveryTerms', e.target.value)}
                      placeholder="e.g., FOB Destination"
                      className={inputCls(false)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={3}
                      placeholder="Additional notes or instructions…"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading && (
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {isEditing ? 'Update Order' : 'Create Order'}
                  </button>
                </div>

              </div>

            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderForm;
